import {
  CheckCircleOutline,
  ErrorOutline,
  WarningAmberOutlined,
  HourglassEmpty,
  BlockOutlined,
  InfoOutlined,
} from "@mui/icons-material";

export const getSelectionStatusConfig = ({
  restricted,
  entry,
  matches,
  tc,
  duplicate,
  company,
  selectedIds,
  selected,
  maxCompanies,
}) => {
  if (restricted) {
    return {
      label: "No access to this YA",
      color: "default",
      icon: <BlockOutlined />,
    };
  }

  if (company?.isExternal && company.externalPending) {
    return {
      label: "Upload required",
      color: "warning",
      icon: <WarningAmberOutlined />,
    };
  }

  if (entry?.loading) {
    return {
      label: "Checking computations…",
      color: "default",
      icon: <HourglassEmpty />,
      loading: true,
    };
  }

  if (entry?.error) {
    return {
      label: "Could not load computations",
      color: "error",
      icon: <ErrorOutline />,
      action: "retry",
    };
  }

  if (!entry) {
    return {
      label: "Checking computations…",
      color: "default",
      icon: <HourglassEmpty />,
      loading: true,
    };
  }

  if (!matches.length) {
    return {
      label: "No supported computation",
      color: "default",
      icon: <InfoOutlined />,
    };
  }

  if (!tc) {
    return {
      label: "Choose a computation",
      color: "warning",
      icon: <WarningAmberOutlined />,
    };
  }

  if (duplicate) {
    return {
      label: "Same UEN already selected",
      color: "warning",
      icon: <WarningAmberOutlined />,
    };
  }

  if (!company.uen) {
    return {
      label: "UEN missing",
      color: "warning",
      icon: <WarningAmberOutlined />,
    };
  }

  if (selectedIds.length >= maxCompanies && !selected) {
    return {
      label: "Company limit reached",
      color: "warning",
      icon: <WarningAmberOutlined />,
    };
  }

  return {
    label: selected ? "Selected" : "Ready to select",
    color: "success",
    icon: <CheckCircleOutline />,
  };
};

export const TAX_COMPUTATION_TYPE = {
  common: "Trading",
  investment: "Investment",
};

export const MAX_COMPANIES = 15;
const SINGAPORE_TIME_ZONE = "Asia/Singapore";
const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const singaporeDateFormatter = new Intl.DateTimeFormat("en-SG", {
  timeZone: SINGAPORE_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

export const dateOnly = (value) => {
  if (value == null || value === "") return "";

  // A date-only value already represents a Singapore calendar date. Parsing it
  // as an instant would unnecessarily make its meaning depend on a time zone.
  if (typeof value === "string" && DATE_ONLY_PATTERN.test(value.trim())) {
    const normalized = value.trim();
    const date = new Date(`${normalized}T00:00:00Z`);
    return Number.isFinite(date.getTime()) &&
      date.toISOString().slice(0, 10) === normalized
      ? normalized
      : "";
  }

  const date = value instanceof Date ? value : new Date(value);
  if (!Number.isFinite(date.getTime())) return "";

  const parts = singaporeDateFormatter.formatToParts(date);
  const part = (type) => parts.find((item) => item.type === type)?.value;
  return `${part("year")}-${part("month")}-${part("day")}`;
};
const parseDateOnly = (value) => {
  const normalized = dateOnly(value);
  if (!DATE_ONLY_PATTERN.test(normalized)) return null;
  const date = new Date(`${normalized}T00:00:00Z`);
  return Number.isFinite(date.getTime()) &&
    date.toISOString().slice(0, 10) === normalized
    ? date
    : null;
};
export function basisPeriod(value) {
  const end = parseDateOnly(value);
  if (!end) return null;
  // Twelve calendar months ending on the selected FYE, including leap years.
  const start = new Date(end);
  start.setUTCDate(start.getUTCDate() + 1);
  const month = start.getUTCMonth();
  start.setUTCFullYear(start.getUTCFullYear() - 1);
  if (start.getUTCMonth() !== month) start.setUTCDate(0);
  return {
    start: start.toISOString().slice(0, 10),
    end: dateOnly(value),
    days: Math.round((end - start) / 86400000) + 1,
  };
}
export function computationPeriod(computation) {
  const start = parseDateOnly(computation?.financialYearStart);
  const end = parseDateOnly(computation?.financialYearEnd);
  if (!start || !end || start > end) return null;
  return {
    start: dateOnly(computation.financialYearStart),
    end: dateOnly(computation.financialYearEnd),
    days: Math.round((end - start) / 86400000) + 1,
  };
}
export function initialCase() {
  const year = new Date().getFullYear();
  return {
    yearOfAssessment: String(year),
    commonFinancialYearEnd: `${year - 1}-12-31`,
    rebatePercentage: "0",
    rebateCap: "0",
  };
}
export function validateSetup(settings) {
  const errors = {};
  const year = Number(settings.yearOfAssessment);
  if (!Number.isInteger(year) || year < 1900 || year > 2200)
    errors.yearOfAssessment = "Enter a valid assessment year.";
  if (!basisPeriod(settings.commonFinancialYearEnd))
    errors.commonFinancialYearEnd = "Choose a valid financial year end.";
  else if (Number(settings.commonFinancialYearEnd.slice(0, 4)) !== year - 1)
    errors.commonFinancialYearEnd =
      "Financial year end must fall in the year before the assessment year.";
  if (
    settings.rebatePercentage === "" ||
    !Number.isFinite(Number(settings.rebatePercentage)) ||
    Number(settings.rebatePercentage) < 0 ||
    Number(settings.rebatePercentage) > 100
  )
    errors.rebatePercentage = "Enter a percentage from 0 to 100.";
  if (
    settings.rebateCap === "" ||
    !Number.isSafeInteger(Number(settings.rebateCap)) ||
    Number(settings.rebateCap) < 0
  )
    errors.rebateCap = "Enter a non-negative whole dollar amount.";
  return errors;
}
export function hasYearAccess(company, year) {
  const permission = company.permission;
  return (
    permission?.role !== "none" &&
    (!permission?.is_partial_access ||
      (permission.allowed_yoa || []).some(
        (item) => Number(item) === Number(year),
      ))
  );
}
export function matchingComputations(computations, settings) {
  const commonFinancialYearEnd = dateOnly(settings?.commonFinancialYearEnd);
  if (!commonFinancialYearEnd) return [];

  return computations.filter(
    (tc) =>
      Number(tc.yearOfAssessment) === Number(settings.yearOfAssessment) &&
      dateOnly(tc.financialYearEnd) === commonFinancialYearEnd &&
      tc.taxPeriodType !== "cross_ya" &&
      tc.status !== "inactive",
  );
}
export function createDraft(company, computation) {
  return {
    id: company.c_id,
    name: company.name,
    uen: company.uen,
    companyType: company.companyType,
    computation,
    shareholdingMaintainedThroughout: "",
    conditionMetOnFinalDay: "",
    continuousQualifyingDays: "",
    sourceFiguresConfirmed: false,
    confirmed: false,
    sourceStatus: "loading",
    sourceNote: "",
    sourceSavedAt: null,
    sourceDoc: null,
  };
}
export function validateDraft(draft) {
  const errors = {};
  if (!draft) return { company: "Choose a company." };
  if (!draft.name?.trim() || !draft.uen?.trim())
    errors.company = "Company name and UEN are required in the company record.";
  if (draft.sourceStatus !== "loaded" || !draft.sourceDoc)
    errors.source = "Load the saved Main Tax Computation before confirming.";
  if (!draft.sourceFiguresConfirmed)
    errors.sourceFiguresConfirmed =
      "Confirm that you checked the source figures.";
  if (draft.shareholdingMaintainedThroughout === "")
    errors.shareholdingMaintainedThroughout =
      "Confirm the shareholding condition.";
  if (draft.shareholdingMaintainedThroughout === false) {
    const periodDays = computationPeriod(draft.computation)?.days || 0;
    if (draft.conditionMetOnFinalDay !== true)
      errors.conditionMetOnFinalDay =
        "The condition must be met on the final day to continue.";
    if (
      !Number.isSafeInteger(Number(draft.continuousQualifyingDays)) ||
      Number(draft.continuousQualifyingDays) < 1 ||
      Number(draft.continuousQualifyingDays) > periodDays
    )
      errors.continuousQualifyingDays =
        "Enter qualifying days within this basis period.";
  }
  return errors;
}
